/**
 * VideoPostMetadataEditor Component
 * 
 * Comprehensive form component for creating and editing video post metadata including:
 * - Basic information (title, video link, language, author)
 * - Thumbnail and original transcript
 * - Content tagging
 * - Publishing status management
 * - Form validation and error handling
 * - Real-time thumbnail preview
 * - Dynamic tag management
 * 
 * Used in: VideoPostList for metadata editing
 */
import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    MenuItem,
    FormControlLabel,
    Switch,
    Chip,
    Alert,
    Avatar,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Save, Cancel, Refresh, Warning } from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import LevelSelector from '../shared/LevelSelector';

const VideoPostMetadataEditor = ({ post, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        title: '',
        link: '',
        language: '',
        level: '',
        author: '',
        thumbUrl: '',
        originalTranscript: '',
        tags: [],
        isReadyToServe: false
    });

    const [languages, setLanguages] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [thumbnailQuality, setThumbnailQuality] = useState('maxresdefault');
    const [readyToServeDialog, setReadyToServeDialog] = useState({ open: false });

    useEffect(() => {
        fetchLanguages();
        if (post) {
            setFormData({
                title: post.title || '',
                link: post.link || '',
                language: post.language?._id || post.language || '',
                level: post.level?._id || post.level || '',
                author: post.author || '',
                thumbUrl: post.thumbUrl || '',
                originalTranscript: post.originalTranscript || '',
                tags: post.tags || [],
                isReadyToServe: post.isReadyToServe || false
            });
        }
    }, [post]);

    const fetchLanguages = async () => {
        try {
            const response = await userRequest.get('/language');
            setLanguages(response.data);
        } catch (error) {
            console.error('Error fetching languages:', error);
        }
    };

    // Extract YouTube video ID from various URL formats
    const extractYouTubeVideoId = (url) => {
        if (!url) return null;
        
        const patterns = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
            /youtube\.com\/v\/([^&\n?#]+)/,
            /youtube\.com\/shorts\/([^&\n?#]+)/
        ];
        
        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match) {
                return match[1];
            }
        }
        return null;
    };

    // Generate YouTube thumbnail URL from video ID
    const generateYouTubeThumbnail = (videoId, quality = 'maxresdefault') => {
        if (!videoId) return '';
        return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
    };

    // Handle thumbnail quality change and auto-regenerate if YouTube URL
    const handleThumbnailQualityChange = (event) => {
        const newQuality = event.target.value;
        setThumbnailQuality(newQuality);
        
        // Auto-regenerate if current thumbnail is from YouTube
        if (formData.thumbUrl && formData.thumbUrl.includes('img.youtube.com')) {
            const videoId = extractYouTubeVideoId(formData.link);
            if (videoId) {
                const thumbnailUrl = generateYouTubeThumbnail(videoId, newQuality);
                setFormData(prev => ({
                    ...prev,
                    thumbUrl: thumbnailUrl
                }));
            }
        }
    };

    // Auto-generate thumbnail when YouTube link is entered
    const handleLinkChange = (event) => {
        const newLink = event.target.value;
        setFormData(prev => ({
            ...prev,
            link: newLink
        }));

        // Auto-generate thumbnail for YouTube videos if thumbnail is empty
        if (newLink && !formData.thumbUrl) {
            const videoId = extractYouTubeVideoId(newLink);
            if (videoId) {
                const thumbnailUrl = generateYouTubeThumbnail(videoId, thumbnailQuality);
                setFormData(prev => ({
                    ...prev,
                    thumbUrl: thumbnailUrl
                }));
            }
        }

        // Clear error when user starts typing
        if (errors.link) {
            setErrors(prev => ({
                ...prev,
                link: null
            }));
        }
    };

    // Manually regenerate thumbnail from current link
    const handleRegenerateThumbnail = () => {
        const videoId = extractYouTubeVideoId(formData.link);
        if (videoId) {
            const thumbnailUrl = generateYouTubeThumbnail(videoId, thumbnailQuality);
            setFormData(prev => ({
                ...prev,
                thumbUrl: thumbnailUrl
            }));
        }
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handleAddTag = () => {
        const tag = tagInput.trim();
        if (tag && !formData.tags.includes(tag)) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, tag]
            }));
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleTagInputKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleAddTag();
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!formData.link.trim()) {
            newErrors.link = 'Video link is required';
        } else if (!isValidUrl(formData.link)) {
            newErrors.link = 'Please enter a valid URL';
        }

        if (!formData.language) {
            newErrors.language = 'Language is required';
        }

        if (!formData.level) {
            newErrors.level = 'Learner level is required';
        }

        if (formData.thumbUrl && !isValidUrl(formData.thumbUrl)) {
            newErrors.thumbUrl = 'Please enter a valid thumbnail URL';
        }

        if (formData.tags.length === 0) {
            newErrors.tags = 'At least one tag is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isValidUrl = (string) => {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            let result;
            if (post) {
                // Update existing video post
                result = await userRequest.patch(`/contentVideoPost/${post._id}`, formData);
            } else {
                // Create new video post
                result = await userRequest.post('/contentVideoPost', formData);
            }
            onSave(result.data);
        } catch (error) {
            console.error('Error saving video post:', error);
            setErrors({ general: error.response?.data?.error || 'Error saving video post' });
        } finally {
            setLoading(false);
        }
    };

    const handleReadyToServeToggle = () => {
        if (!formData.isReadyToServe) {
            // User is trying to set to ready - show warning dialog
            setReadyToServeDialog({ open: true });
        } else {
            // User is turning it off - allow immediately
            setFormData(prev => ({
                ...prev,
                isReadyToServe: false
            }));
        }
    };

    const handleReadyToServeConfirm = (confirmed) => {
        setReadyToServeDialog({ open: false });
        if (confirmed) {
            setFormData(prev => ({
                ...prev,
                isReadyToServe: true
            }));
        }
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">
                    {post ? 'Edit Video Post Metadata' : 'Create Video Post'}
                </Typography>
                <Box>
                    <Button 
                        onClick={onCancel} 
                        sx={{ mr: 2 }}
                        startIcon={<Cancel />}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleSubmit}
                        disabled={loading}
                        startIcon={<Save />}
                    >
                        {loading ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Box>

            <Paper sx={{ p: 3 }}>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        {errors.general && (
                            <Grid item xs={12}>
                                <Alert severity="error">{errors.general}</Alert>
                            </Grid>
                        )}

                        {/* Basic Information */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Basic Information
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Video Title"
                                value={formData.title}
                                onChange={handleInputChange('title')}
                                error={!!errors.title}
                                helperText={errors.title}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                label="Language"
                                value={formData.language}
                                onChange={handleInputChange('language')}
                                error={!!errors.language}
                                helperText={errors.language}
                                required
                            >
                                {languages.map((lang) => (
                                    <MenuItem key={lang._id} value={lang._id}>
                                        {lang.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <LevelSelector
                                value={formData.level}
                                onChange={handleInputChange('level')}
                                error={errors.level}
                                required
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Video Link"
                                value={formData.link}
                                onChange={handleLinkChange}
                                error={!!errors.link}
                                helperText={errors.link ? errors.link : "Supports YouTube, YouTube Shorts, and custom URLs. Thumbnails auto-generated for YouTube."}
                                placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Author"
                                value={formData.author}
                                onChange={handleInputChange('author')}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <TextField
                                    fullWidth
                                    label="Thumbnail URL"
                                    value={formData.thumbUrl}
                                    onChange={handleInputChange('thumbUrl')}
                                    error={!!errors.thumbUrl}
                                    helperText={errors.thumbUrl || "Auto-generated from YouTube link or enter custom URL"}
                                    placeholder="Auto-generated or custom URL"
                                />
                                {extractYouTubeVideoId(formData.link) && (
                                    <>
                                        <TextField
                                            select
                                            size="small"
                                            value={thumbnailQuality}
                                            onChange={handleThumbnailQualityChange}
                                            sx={{ 
                                                width: '120px',
                                                mt: errors.thumbUrl ? -2.5 : 0 
                                            }}
                                        >
                                            <MenuItem value="maxresdefault">Max Quality</MenuItem>
                                            <MenuItem value="hqdefault">High Quality</MenuItem>
                                            <MenuItem value="mqdefault">Medium Quality</MenuItem>
                                            <MenuItem value="sddefault">Standard Quality</MenuItem>
                                        </TextField>
                                        <Tooltip title="Regenerate YouTube thumbnail">
                                            <IconButton 
                                                onClick={handleRegenerateThumbnail}
                                                color="primary"
                                                sx={{ mt: errors.thumbUrl ? -2.5 : 0 }}
                                            >
                                                <Refresh />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}
                            </Box>
                        </Grid>

                        {/* Thumbnail Preview */}
                        <Grid item xs={12}>
                            <Box sx={{ mb: 2 }}>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <Typography variant="subtitle2">
                                        Thumbnail Preview
                                    </Typography>
                                    {formData.thumbUrl && extractYouTubeVideoId(formData.link) && 
                                     formData.thumbUrl.includes('img.youtube.com') && (
                                        <Chip 
                                            label="Auto-generated" 
                                            size="small" 
                                            color="primary" 
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                                {formData.thumbUrl ? (
                                    <Box position="relative" display="inline-block">
                                        <Avatar
                                            src={formData.thumbUrl}
                                            variant="square"
                                            sx={{ 
                                                width: 160, 
                                                height: 90,
                                                border: '1px solid',
                                                borderColor: 'divider'
                                            }}
                                        />
                                        {formData.thumbUrl.includes('img.youtube.com') && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: 4,
                                                    right: 4,
                                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                                    color: 'white',
                                                    px: 1,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                YouTube
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            width: 160,
                                            height: 90,
                                            border: '2px dashed',
                                            borderColor: 'divider',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 1,
                                            backgroundColor: 'grey.50'
                                        }}
                                    >
                                        <Box textAlign="center">
                                            <Typography variant="body2" color="text.secondary">
                                                No Thumbnail
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {extractYouTubeVideoId(formData.link) ? 
                                                    'Click refresh to generate' : 
                                                    'Enter YouTube URL or custom thumbnail'
                                                }
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {/* Original Transcript */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Original Transcript (Optional)
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={6}
                                label="Original Transcript"
                                value={formData.originalTranscript}
                                onChange={handleInputChange('originalTranscript')}
                                placeholder="Paste the complete video transcript here (optional). You can break this into timed segments later."
                            />
                        </Grid>

                        {/* Tags */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Tags
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Box display="flex" gap={1} alignItems="center" mb={2}>
                                <TextField
                                    label="Add Tag"
                                    value={tagInput}
                                    onChange={(e) => setTagInput(e.target.value)}
                                    onKeyPress={handleTagInputKeyPress}
                                    size="small"
                                />
                                <Button 
                                    onClick={handleAddTag}
                                    variant="outlined"
                                    disabled={!tagInput.trim()}
                                >
                                    Add
                                </Button>
                            </Box>
                            
                            {errors.tags && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {errors.tags}
                                </Alert>
                            )}

                            <Box display="flex" gap={1} flexWrap="wrap">
                                {formData.tags.map((tag, index) => (
                                    <Chip
                                        key={index}
                                        label={tag}
                                        onDelete={() => handleRemoveTag(tag)}
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Grid>

                        {/* Publishing Status */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                Publishing
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Publication Settings
                                </Typography>
                                
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Note:</strong> Translation checking is not available at the metadata level. 
                                        Please ensure all transcript segments have been properly translated before marking as ready to serve.
                                    </Typography>
                                </Alert>

                                {/* Ready to Serve Toggle */}
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isReadyToServe}
                                            onChange={handleReadyToServeToggle}
                                            name="isReadyToServe"
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body1">
                                                Ready to Serve
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formData.isReadyToServe 
                                                    ? "This video is ready for users to view" 
                                                    : "This video is not ready for users to view"
                                                }
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Paper>

            {/* Ready to Serve Confirmation Dialog */}
            <Dialog
                open={readyToServeDialog.open}
                onClose={() => handleReadyToServeConfirm(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Warning color="warning" />
                        Confirm Ready to Serve
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Before marking this video as "Ready to Serve", please ensure you have:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mt: 2 }}>
                        <li>
                            <Typography variant="body2">
                                Created and properly segmented all transcripts
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                Translated all transcript segments completely
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                Verified video metadata (title, tags, thumbnail) is correct
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                Tested the video content for quality and completeness
                            </Typography>
                        </li>
                    </Box>
                    <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold' }}>
                        Have you completed these checks and is the video ready for users?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleReadyToServeConfirm(false)}>
                        No, I need to check more
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => handleReadyToServeConfirm(true)}
                    >
                        Yes, it is ready
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VideoPostMetadataEditor; 